import { useState, useMemo, useRef, useEffect } from 'react';
import { Search, X, ChevronDown, Sparkles } from 'lucide-react';
import { Input } from '../components/ui/input';

const CATEGORY_COLORS = {
  machine: '#3b82f6',
  poids_libres: '#f59e0b',
  poids_du_corps: '#10b981',
  cardio: '#ef4444',
  fonctionnel: '#8b5cf6',
  halterophilie: '#ec4899',
  abdos_core: '#06b6d4',
  stretching: '#84cc16',
  sport: '#f97316',
};

export default function ExerciseSearchSelect({ catalog, selectedCategory, selectedExercise, onCategoryChange, onExerciseChange, onCreateCustom }) {
  const [search, setSearch] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [focusedCat, setFocusedCat] = useState(selectedCategory);
  const dropdownRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    function handleClickOutside(e) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) setIsOpen(false);
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const allExercises = useMemo(() => {
    if (!catalog) return [];
    const all = [];
    for (const [catKey, catData] of Object.entries(catalog)) {
      for (const ex of catData.exercises || []) {
        all.push({ ...ex, category: catKey, categoryLabel: catData.label });
      }
    }
    return all;
  }, [catalog]);

  const filtered = useMemo(() => {
    const q = search.toLowerCase().trim();
    let items = allExercises;
    if (focusedCat && !q) {
      items = items.filter(e => e.category === focusedCat);
    }
    if (q) {
      items = items.filter(e =>
        e.name.toLowerCase().includes(q) ||
        e.muscle.toLowerCase().includes(q) ||
        e.categoryLabel?.toLowerCase().includes(q)
      );
    }
    return items.slice(0, 50);
  }, [allExercises, search, focusedCat]);

  const selectedInfo = allExercises.find(e => e.id === selectedExercise);

  const handleSelect = (ex) => {
    onCategoryChange(ex.category);
    onExerciseChange(ex.id);
    setSearch('');
    setIsOpen(false);
  };

  const categoryCounts = useMemo(() => {
    if (!catalog) return {};
    const counts = {};
    for (const [key, data] of Object.entries(catalog)) {
      counts[key] = data.exercises?.length || 0;
    }
    return counts;
  }, [catalog]);

  return (
    <div className="space-y-3" ref={dropdownRef}>
      {/* Category pills - horizontal scroll */}
      <div className="flex gap-1.5 overflow-x-auto pb-1 scrollbar-hide" data-testid="category-pills">
        {catalog && Object.entries(catalog).map(([key, data]) => (
          <button
            key={key}
            type="button"
            onClick={() => { setFocusedCat(key); onCategoryChange(key); setIsOpen(true); }}
            className={`flex-shrink-0 px-2.5 py-1.5 rounded-full text-[11px] font-medium transition-all whitespace-nowrap ${
              (focusedCat === key || selectedCategory === key)
                ? 'text-black'
                : 'bg-[#1B1B1B] text-zinc-400 border border-[#2A2A2E] hover:border-zinc-600'
            }`}
            style={(focusedCat === key || selectedCategory === key) ? { backgroundColor: CATEGORY_COLORS[key] || '#666' } : {}}
            data-testid={`cat-pill-${key}`}
          >
            {data.label} <span className="opacity-60 ml-0.5">{categoryCounts[key]}</span>
          </button>
        ))}
      </div>

      {/* Search input + selected display */}
      <div className="relative">
        {!isOpen && selectedInfo ? (
          <button
            type="button"
            onClick={() => { setIsOpen(true); setTimeout(() => inputRef.current?.focus(), 50); }}
            className="w-full flex items-center gap-2 bg-[#0F0F0F] border border-[#2A2A2E] text-white rounded-sm px-3 py-2.5 text-sm text-left hover:border-zinc-600 transition-colors"
            data-testid="exercise-select-display"
          >
            <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: CATEGORY_COLORS[selectedInfo.category] }} />
            <span className="flex-1 truncate">{selectedInfo.name}</span>
            <span className="text-[10px] text-zinc-500 flex-shrink-0">{selectedInfo.muscle} · {selectedInfo.unit}</span>
            <ChevronDown className="w-3.5 h-3.5 text-zinc-500" />
          </button>
        ) : (
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
            <Input
              ref={inputRef}
              type="text"
              value={search}
              onChange={(e) => { setSearch(e.target.value); setIsOpen(true); if (e.target.value) setFocusedCat(''); }}
              onFocus={() => setIsOpen(true)}
              placeholder="Rechercher un exercice (nom, muscle, type)..."
              className="pl-9 pr-8 bg-[#0F0F0F] border-[#2A2A2E] text-white text-sm"
              data-testid="exercise-search-input"
            />
            {search && (
              <button type="button" onClick={() => { setSearch(''); setFocusedCat(selectedCategory); }} className="absolute right-3 top-1/2 -translate-y-1/2">
                <X className="w-3.5 h-3.5 text-zinc-500 hover:text-white" />
              </button>
            )}
          </div>
        )}

        {/* Dropdown results */}
        {isOpen && (
          <div className="absolute z-50 w-full mt-1 bg-[#111118] border border-[#2A2A2E] rounded-sm max-h-[280px] overflow-y-auto shadow-xl" data-testid="exercise-dropdown">
            {filtered.length === 0 && (
              <div className="p-4 text-center">
                <p className="text-sm text-zinc-500">Aucun exercice trouve</p>
                {onCreateCustom && (
                  <button
                    type="button"
                    onClick={() => { onCreateCustom(search); setIsOpen(false); }}
                    className="mt-2 text-xs text-[#FF6B35] hover:underline flex items-center gap-1 mx-auto"
                    data-testid="create-custom-from-search"
                  >
                    <Sparkles className="w-3 h-3" /> Creer &laquo;{search}&raquo; comme exercice personnalise
                  </button>
                )}
              </div>
            )}
            {filtered.map((ex) => (
              <button
                key={ex.id}
                type="button"
                onClick={() => handleSelect(ex)}
                className={`w-full flex items-center gap-2 px-3 py-2 text-left text-sm hover:bg-[#1B1B1B] transition-colors ${
                  ex.id === selectedExercise ? 'bg-[#FF6B35]/10 text-[#FF6B35]' : 'text-white'
                }`}
                data-testid={`exercise-option-${ex.id}`}
              >
                <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: CATEGORY_COLORS[ex.category] }} />
                <span className="flex-1 truncate">{ex.name}</span>
                <span className="text-[10px] text-zinc-500 flex-shrink-0">{ex.muscle}</span>
                <span className="text-[10px] text-zinc-600 flex-shrink-0 w-8 text-right">{ex.unit}</span>
                {ex.is_custom && <Sparkles className="w-3 h-3 text-[#FF6B35] flex-shrink-0" />}
              </button>
            ))}
            {filtered.length > 0 && onCreateCustom && (
              <button
                type="button"
                onClick={() => { onCreateCustom(search); setIsOpen(false); setSearch(''); }}
                className="w-full px-3 py-2.5 text-left text-xs text-[#FF6B35] hover:bg-[#1B1B1B] border-t border-[#2A2A2E] flex items-center gap-2"
                data-testid="create-custom-button"
              >
                <Sparkles className="w-3.5 h-3.5" /> Creer un exercice personnalise
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
